import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  FileText, 
  ExternalLink, 
  Brain, 
  Globe, 
  Search, 
  TrendingUp,
  Database,
  Target
} from 'lucide-react';

interface SourceReference {
  type: 'current_page' | 'other_page' | 'adjacent_topic' | 'sector_topic' | 'contextualized_research';
  pageId?: string;
  anchor?: string;
  url?: string;
  title?: string;
  snippet: string;
  content?: string; // Full content if available
  relevanceScore: number;
  topicName?: string;
  topicId?: string;
  researchId?: string;
  documentId?: string;
}

interface RagSourcesViewerProps {
  sources: SourceReference[];
  confidence?: number;
  className?: string;
}

export const RagSourcesViewer: React.FC<RagSourcesViewerProps> = ({ 
  sources, 
  confidence,
  className = '' 
}) => {
  // Filter out current_page sources as they are not actual RAG documents
  const ragSources = sources?.filter(source => 
    source.type === 'sector_topic' || 
    source.type === 'contextualized_research' ||
    source.type === 'adjacent_topic'
  ) || [];

  if (!ragSources || ragSources.length === 0) {
    return (
      <Card className={`${className} bg-gray-50`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-center text-gray-500">
            <Database className="h-5 w-5 mr-2" />
            <span>No RAG sources were used for this response</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getSourceIcon = (type: string) => {
    switch (type) {
      case 'current_page':
        return <FileText className="h-4 w-4" />;
      case 'other_page':
        return <ExternalLink className="h-4 w-4" />;
      case 'adjacent_topic':
        return <Brain className="h-4 w-4" />;
      case 'sector_topic':
        return <Globe className="h-4 w-4" />;
      case 'contextualized_research':
        return <Search className="h-4 w-4" />;
      default:
        return <Database className="h-4 w-4" />;
    }
  };

  const getSourceColor = (type: string) => {
    switch (type) {
      case 'current_page':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'other_page':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'adjacent_topic':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'sector_topic':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'contextualized_research':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSourceTypeName = (type: string) => {
    switch (type) {
      case 'current_page':
        return 'Current Page';
      case 'other_page':
        return 'Other Page';
      case 'adjacent_topic':
        return 'Adjacent Topic';
      case 'sector_topic':
        return 'Sector Topic';
      case 'contextualized_research':
        return 'Research Data';
      default:
        return 'Unknown';
    }
  };

  const getRelevanceScoreColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600 bg-green-50';
    if (score >= 0.6) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  // Sort sources by relevance score
  const sortedSources = [...ragSources].sort((a, b) => b.relevanceScore - a.relevanceScore);

  // Calculate statistics
  const avgRelevance = ragSources.reduce((sum, s) => sum + s.relevanceScore, 0) / ragSources.length;
  const sourceTypes = ragSources.reduce((acc, source) => {
    const type = source.type;
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center">
            <Target className="h-5 w-5 mr-2" />
            RAG Sources ({ragSources.length})
          </CardTitle>
          {confidence && (
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium">
                Confidence: {(confidence * 100).toFixed(1)}%
              </span>
            </div>
          )}
        </div>
        
        {/* Source statistics */}
        <div className="flex flex-wrap gap-2 mt-2">
          <Badge variant="outline" className="text-xs">
            Avg. Relevance: {avgRelevance.toFixed(3)}
          </Badge>
          {Object.entries(sourceTypes).map(([type, count]) => (
            <Badge 
              key={type} 
              variant="outline" 
              className={`text-xs ${getSourceColor(type)}`}
            >
              {getSourceTypeName(type)}: {count}
            </Badge>
          ))}
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        <div className="space-y-3">
          {sortedSources.map((source, index) => (
            <div key={index} className="border rounded-lg p-3 bg-gray-50">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-2">
                  {getSourceIcon(source.type)}
                  <Badge className={`text-xs ${getSourceColor(source.type)}`}>
                    {getSourceTypeName(source.type)}
                  </Badge>
                  {source.topicName && (
                    <Badge variant="outline" className="text-xs">
                      {source.topicName}
                    </Badge>
                  )}
                </div>
                
                <div className={`px-2 py-1 rounded text-xs font-medium ${getRelevanceScoreColor(source.relevanceScore)}`}>
                  {source.relevanceScore.toFixed(3)}
                </div>
              </div>
              
              {source.title && (
                <div className="font-medium text-sm mb-1">{source.title}</div>
              )}
              
              {/* Show full content or snippet */}
              <div className="text-sm text-gray-700 mb-2">
                {source.content ? (
                  <details className="cursor-pointer">
                    <summary className="font-medium hover:text-gray-900">
                      {source.snippet}
                      <span className="ml-2 text-xs text-blue-600">[Click to expand full content]</span>
                    </summary>
                    <div className="mt-2 pl-4 border-l-2 border-gray-300 text-xs leading-relaxed whitespace-pre-wrap">
                      {source.content}
                    </div>
                  </details>
                ) : (
                  source.snippet
                )}
              </div>
              
              {/* Links and references */}
              <div className="flex flex-wrap gap-2 text-xs">
                {source.url && (
                  <a 
                    href={source.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center text-blue-600 hover:underline"
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    View Page
                  </a>
                )}
                
                {source.researchId && (
                  <a 
                    href={`/research-documents?id=${source.researchId}`}
                    className="flex items-center text-purple-600 hover:underline"
                  >
                    <FileText className="h-3 w-3 mr-1" />
                    View Research Document
                  </a>
                )}
                
                {source.topicId && (
                  <a 
                    href={`/site-topics?topicId=${source.topicId}`}
                    className="flex items-center text-green-600 hover:underline"
                  >
                    <Brain className="h-3 w-3 mr-1" />
                    View Topic
                  </a>
                )}
                
                {source.pageId && !source.url && (
                  <span className="text-gray-500">
                    Page ID: {source.pageId}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default RagSourcesViewer;